import { createFileRoute, Link } from "@tanstack/react-router";
import { hreflangLinks, articleMeta, absoluteUrl } from "@/lib/seo";
import { articleHeroFor } from "@/lib/article-heroes";
import { BlogPost, articleJsonLd, breadcrumbJsonLd } from "@/components/BlogPost";

const SLUG = "pikler-kiipeilyteline-opas";
const URL = `https://montessori24.fi/montessori-koulu/${SLUG}`;
const TITLE = "Pikler-kiipeilyteline — Täydellinen ostajan opas (2026)";
const DESC = "Pikler-kiipeilyteline lapselle: ikäsuositukset, turvallisuus, mallit (kolmio, kaari, ramppi) ja ostovinkit. Suomalainen opas vanhemmille.";
const PUBLISHED = "2026-06-18";
const HERO = articleHeroFor(SLUG);

export const Route = createFileRoute("/montessori-koulu/pikler-kiipeilyteline-opas")({
  head: () => ({
    meta: articleMeta({
      slug: SLUG,
      title: TITLE,
      description: DESC,
      published: PUBLISHED,
      image: HERO,
    }),
    links: [{ rel: "canonical", href: URL }, ...hreflangLinks(URL)],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(articleJsonLd({ title: TITLE, description: DESC, url: URL, image: absoluteUrl(HERO), publishedISO: PUBLISHED })) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd(SLUG, TITLE)) },
    ],
  }),
  component: Post,
});

function Post() {
  return (
    <BlogPost
      eyebrow="Pikler-opas"
      title={TITLE}
      lead="Pikler-kiipeilyteline on yksi parhaista investoinneista lapsen ensimmäisten kuuden vuoden ajalle. Tämä opas kertoo, miten valitset oikean mallin, miten varmistat turvallisuuden ja miten teline kasvaa lapsesi mukana."
      readingMinutes={9}
      publishedISO={PUBLISHED}
    >
      <h2>Mikä on Pikler-kiipeilyteline?</h2>
      <p>Pikler-kiipeilyteline pohjautuu unkarilaisen lastenlääkärin <strong>Emmi Piklerin</strong> 1940-luvulla kehittämään pedagogiikkaan. Perusajatus on yksinkertainen: lapsi liikkuu omassa tahdissaan ja kiipeää vain niin korkealle kuin osaa itse laskeutua. Aikuinen ei nosta, ei auta — vain turvaa.</p>
      <p>Tyypillinen Pikler-mallisto sisältää kolme elementtiä: <strong>kiipeilykolmio</strong> (klassinen A-teline), <strong>kiipeilykaari</strong> (puolikuun muotoinen rocker) ja <strong>ramppi/liukumäki</strong>, joka kiinnittyy kolmioon tai kaareen.</p>

      <h2>Mitä hyötyä Pikler-telineestä on?</h2>
      <ul>
        <li><strong>Karkeamotoriikka & vartalonhallinta</strong> — keskivartalo, kädet ja jalat saavat töitä jokaisella askelmalla.</li>
        <li><strong>Tasapaino & koordinaatio</strong> — eri kallistuskulmat haastavat vestibulaarijärjestelmää.</li>
        <li><strong>Riskien arviointi</strong> — lapsi oppii lukemaan omat rajansa, mikä vähentää loukkaantumisia myöhemmin.</li>
        <li><strong>Itseluottamus</strong> — ”minä osaan itse” -kokemus on Montessorin ydin.</li>
        <li><strong>Keskittyminen</strong> — toistuva haastava liike rauhoittaa ja kehittää tarkkaavaisuutta.</li>
      </ul>

      <h2>Ikäsuositukset: 6 kk → 6 v</h2>
      <p>Pikler-teline on harvinainen lasten tuote, joka palvelee koko varhaislapsuuden. Karkea jako:</p>
      <ul>
        <li><strong>6–12 kk:</strong> lapsi tutkii telinettä alta, vetää itseään seisomaan ja oppii tippumaan turvallisesti pehmeälle alustalle.</li>
        <li><strong>1–2 v:</strong> ensimmäiset kiipeämiset alimmille askelmille; ramppi houkuttelee ryömimään ylös ja liukumaan alas.</li>
        <li><strong>2–4 v:</strong> koko teline käytössä, lapsi yhdistelee kaarta ja kolmiota itse.</li>
        <li><strong>4–6 v:</strong> teline muuttuu majaksi, ninja-radaksi ja näyttämöksi — käyttö muuttuu mielikuvitusleikiksi.</li>
      </ul>

      <h2>Pikler-kolmio vai kiipeilykaari — kumpi ensin?</h2>
      <p>Jos hankit vain yhden, valitse <strong>kolmio</strong>. Se on vakaampi, helppo ensimmäinen kiivettävä ja toimii heti vauvaikäisille tuettuna seisonta-alustana. Kaari toimii loistavasti 8 kk+ keinuna, mutta on toissijainen ensiostokseksi. Yhdistettynä rampin kanssa saat kolmiosta yli kymmenen leikkivariaatiota.</p>

      <h2>Turvallisuus — mihin kiinnittää huomiota</h2>
      <ol>
        <li><strong>Turvallisuustiedot:</strong> CE-merkintä ja valmistajan ilmoittamat testaustiedot (esim. EN 71). Pyydä tiedot, jos niitä ei ole näkyvissä.</li>
        <li><strong>Materiaali:</strong> koivuvaneri (mielellään FSC), turvalliset vesipohjaiset viimeistelyt. Vältä mäntyä — halkeilee.</li>
        <li><strong>Lukot:</strong> teräslukot askelmien päissä estävät kolmion romahtamisen.</li>
        <li><strong>Pehmeä alusta:</strong> matto tai pelimatto telineen alle. Koviin lattioihin emme suosittele.</li>
        <li><strong>Painoraja:</strong> laadukas teline kestää 60 kg — silloin myös aikuinen voi ohjata leikkiä.</li>
      </ol>

      <h2>Hinnoittelu: mitä Pikler maksaa Suomessa?</h2>
      <p>Edullisimmat kolmiot maksavat 80–120 €, mutta koivuvanerista valmistettu malli alkaa <strong>noin 150 €:sta</strong>. Kaari 130–200 €, ramppi 80–140 €. Kokonainen 3-osainen setti maksaa 350–500 €. Hinta tuntuu suurelta — mutta laske: 6 vuotta käyttöä = noin <strong>1 € viikossa</strong>, ja teline kiertää usein useammalla sisaruksella.</p>

      <h2>Yleisimmät kysymykset</h2>
      <h3>Sopiiko Pikler pieneen asuntoon?</h3>
      <p>Kyllä. Kolmio taittuu kasaan ja vie säilytyksessä noin 90 × 15 cm. Käytössä se mahtuu olohuoneen nurkkaan tai lastenhuoneeseen.</p>
      <h3>Pitääkö lasta opettaa käyttämään?</h3>
      <p>Ei. Montessori-tapa: jätä teline saataville, älä komenna mukaan ja anna lapsen aloittaa itse. Älä koskaan nosta lasta korkeammalle kuin minne hän itse kiipeää.</p>
      <h3>Mistä tunnistan huonon Pikler-kopion?</h3>
      <p>Liian kevyt (alle 8 kg), karkeat hiomattomat reunat, näkyvät ruuvit askelmissa, ei sertifikaattimerkintää tai myyjä ei osaa kertoa materiaalia.</p>

      <h2>Yhteenveto — kannattaako Pikler?</h2>
      <p>Jos haluat yhden kalusteen, joka kasvaa vauvasta esikouluikäiseen ja tukee lasta sekä motorisesti että psyykkisesti, Pikler on lähes ylivertainen valinta. Aloita yhdellä sertifioidulla kolmiolla ja lisää kaari + ramppi, kun lapsi on valmis.</p>
      <p>Tutustu Montessori24:n Pikler-mallistoon — tuotekohtaiset turvallisuustiedot löydät jokaiselta tuotesivulta: <Link to="/kauppa" search={{ kategoria: "climbing" }}>Selaa Pikler-kiipeilytelineitä →</Link></p>
    </BlogPost>
  );
}